// ============================================================
// WeaponOverridesEditor - Second modifier row for per-attack
// weapon overrides: ±X d10 damage, ±X penetration, damage type
// swap, and special-rule add/upgrade. Pure controlled component
// over a WeaponOverrides value; the transform itself lives in
// engine/weaponOverrides.ts.
// ============================================================
import { memo, useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/WeaponOverridesEditor.module.css';
import { DAMAGE_TYPE_COLORS } from '../engine/constants';
import { WS_LIST, groupByFamily, parseWS, fmtWS, wsLabel } from '../engine/weaponSpecials';
import { EMPTY_WEAPON_OVERRIDES, hasActiveOverrides } from '../engine/weaponOverrides';
import type { Slot, WeaponOverrides } from '../types';
import NumberInput from './NumberInput';
import { onActivateKey } from '../utils/keyActivation';

interface WeaponOverridesEditorProps {
  slot: Slot;
  value: WeaponOverrides;
  onChange: (next: WeaponOverrides) => void;
  disabled?: boolean;
}

const DAMAGE_TYPES: Array<Slot['damageType']> = ['I', 'E', 'R', 'X', 'A'];

const fmtSigned = (n: number): string => (n >= 0 ? `+${n}` : String(n));

/** Green for a bonus, red for a penalty, the default for none. */
const signColour = (n: number): string | undefined =>
  n > 0 ? 'var(--ok-soft)' : n < 0 ? 'var(--danger-soft)' : undefined;

function WeaponOverridesEditor({ slot, value, onChange, disabled }: WeaponOverridesEditorProps) {
  const set = (patch: Partial<WeaponOverrides>) => onChange({ ...value, ...patch });
  const flat = value.damageFlat || 0;
  return (
    <div className={styles.container} data-testid="weapon-overrides-editor">
      <div className={styles.headerRow}>
        <span className={styles.title}>Attack Overrides</span>
        {hasActiveOverrides(value) && (
          <button
            type="button"
            className={common.btnSmall}
            style={{ fontSize: 10, color: 'var(--danger)' }}
            disabled={disabled}
            onClick={() => onChange({ ...EMPTY_WEAPON_OVERRIDES, specials: [] })}
          >
            Reset
          </button>
        )}
      </div>

      {/* Numeric steppers + damage type */}
      <div className={styles.stepperRow}>
        <span className={styles.stepperGroup}>
          <span className={styles.stepperLabel}>Dmg</span>
          <Stepper
            what={['Remove d10 damage', 'Add d10 damage']}
            testId="override-damage-dice"
            value={value.damageDice}
            suffix="d10"
            disabled={disabled}
            onChange={(damageDice) => set({ damageDice })}
          />
          <Stepper
            what={['Decrease flat damage', 'Increase flat damage']}
            testId="override-damage-flat"
            value={flat}
            disabled={disabled}
            onChange={(damageFlat) => set({ damageFlat })}
          />
        </span>
        <span className={styles.stepperGroup}>
          <span className={styles.stepperLabel}>Pen</span>
          <Stepper
            what={['Decrease penetration', 'Increase penetration']}
            testId="override-pen-delta"
            value={value.penDelta}
            disabled={disabled}
            onChange={(penDelta) => set({ penDelta })}
          />
        </span>
        <DamageTypeSelect slot={slot} value={value} disabled={disabled} onChange={set} />
      </div>
      {value.specials.length > 0 && <OverrideChips slot={slot} value={value} disabled={disabled} onChange={set} />}
      <AddSpecialRow slot={slot} value={value} disabled={disabled} onChange={set} />
    </div>
  );
}

interface StepperProps {
  /** The minus and plus buttons' accessible names. */
  what: [string, string];
  testId: string;
  value: number;
  suffix?: string;
  disabled?: boolean;
  onChange: (next: number) => void;
}

/** −, the signed value (tinted by its sign), +. */
function Stepper({ what: [less, more], testId, value, suffix = '', disabled, onChange }: StepperProps) {
  return (
    <>
      <button
        type="button"
        className={styles.stepBtn}
        aria-label={less}
        disabled={disabled}
        onClick={() => onChange(value - 1)}
      >
        −
      </button>
      <span className={styles.stepValue} data-testid={testId} style={{ color: signColour(value) }}>
        {fmtSigned(value)}
        {suffix}
      </span>
      <button
        type="button"
        className={styles.stepBtn}
        aria-label={more}
        disabled={disabled}
        onClick={() => onChange(value + 1)}
      >
        +
      </button>
    </>
  );
}

interface PartProps {
  slot: Slot;
  value: WeaponOverrides;
  disabled?: boolean;
  onChange: (patch: Partial<WeaponOverrides>) => void;
}

/** The weapon's own damage type first (no override), then the others. */
function DamageTypeSelect({ slot, value, disabled, onChange }: PartProps) {
  return (
    <span className={styles.stepperGroup}>
      <span className={styles.stepperLabel}>Type</span>
      <select
        className={common.input}
        style={{ fontSize: 10, padding: '1px 4px' }}
        aria-label="Damage type override"
        disabled={disabled}
        value={value.damageTypeOverride ?? ''}
        onChange={(e) =>
          onChange({ damageTypeOverride: (e.target.value || null) as WeaponOverrides['damageTypeOverride'] })
        }
      >
        <option value="">{DAMAGE_TYPE_COLORS[slot.damageType]?.label || slot.damageType} (weapon)</option>
        {DAMAGE_TYPES.filter((t) => t !== slot.damageType).map((t) => (
          <option key={t} value={t}>
            {DAMAGE_TYPE_COLORS[t]?.label || t}
          </option>
        ))}
      </select>
    </span>
  );
}

/** The override specials; one that upgrades the weapon's own reads "Proven (3)→Proven (4)". */
function OverrideChips({ slot, value, disabled, onChange }: PartProps) {
  return (
    <div className={styles.chipRow}>
      {value.specials.map((sp) => {
        const { id } = parseWS(sp);
        const existing = (slot.specialRules || []).find((b) => parseWS(b).id === id);
        const remove = () => onChange({ specials: value.specials.filter((s) => s !== sp) });
        return (
          <span key={sp} className={styles.chip}>
            {existing && existing !== sp ? `${fmtWS(existing)}→${fmtWS(sp)}` : fmtWS(sp)}
            {!disabled && (
              <span
                className={styles.chipRemove}
                role="button"
                tabIndex={0}
                aria-label={`Remove override ${fmtWS(sp)}`}
                onClick={remove}
                onKeyDown={onActivateKey(remove)}
              >
                ✕
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

/** Pick a special (with its parameter when it takes one) and add it, replacing any of the same id. */
function AddSpecialRow({ slot, value, disabled, onChange }: PartProps) {
  const [specialId, setSpecialId] = useState('');
  const [specialParam, setSpecialParam] = useState(1);
  const isParameterized = (WS_LIST.find(([id]) => id === specialId)?.[1] ?? 0) > 0;
  const addSpecial = () => {
    if (!specialId) return;
    const str = isParameterized ? `${specialId}(${Math.max(1, specialParam)})` : specialId;
    const withoutSameId = value.specials.filter((sp) => parseWS(sp).id !== specialId);
    onChange({ specials: [...withoutSameId, str] });
    setSpecialId('');
    setSpecialParam(1);
  };
  const choose = (id: string) => {
    setSpecialId(id);
    setSpecialParam(defaultParam(id));
  };
  return (
    <div className={styles.addRow}>
      <SpecialSelect slot={slot} value={specialId} disabled={disabled} onChoose={choose} />
      {isParameterized && (
        <NumberInput
          className={common.input}
          style={{ width: 40, fontSize: 10, textAlign: 'center' }}
          min={1}
          aria-label="Special rule parameter"
          disabled={disabled}
          value={specialParam}
          onChange={(v) => setSpecialParam(v)}
          onKeyDown={(e) => e.key === 'Enter' && addSpecial()}
        />
      )}
      <button
        type="button"
        className={common.btnSmall}
        style={{ fontSize: 10 }}
        disabled={disabled || !specialId}
        onClick={addSpecial}
      >
        Add
      </button>
    </div>
  );
}

/** A special's default parameter: its own when it takes one, else 1. */
function defaultParam(id: string): number {
  const def = WS_LIST.find(([wid]) => wid === id);
  return def && def[1] > 0 ? def[1] : 1;
}

interface SpecialSelectProps {
  slot: Slot;
  value: string;
  disabled?: boolean;
  onChoose: (id: string) => void;
}

/** Every special, by family; the weapon's own are marked. */
function SpecialSelect({ slot, value, disabled, onChoose }: SpecialSelectProps) {
  return (
    <select
      className={common.input}
      style={{ fontSize: 10, padding: '1px 4px', flex: 1, minWidth: 0 }}
      aria-label="Add special rule"
      disabled={disabled}
      value={value}
      onChange={(e) => onChoose(e.target.value)}
    >
      <option value="">+ Special rule…</option>
      {groupByFamily(WS_LIST).map(({ family, rows }) => (
        <optgroup key={family.id} label={family.label}>
          {rows.map(([id]) => (
            <option key={id} value={id}>
              {wsLabel(id)}
              {(slot.specialRules || []).some((b) => parseWS(b).id === id) ? ' (on weapon)' : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default memo(WeaponOverridesEditor);
